import React from 'react';

import { useConfig } from '../../../../defaultConfig';
import { useTransferAccountsContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { isValidAccountTransferFrom, isValidAccountTransferTo } from '../../../../services/accountFilters';
import { NoteTypes } from '../../../../translations/translationType';
import filterInvalidAccounts from '../../../../utils/filterInvalidAccounts';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { DateField } from '../../../shared/DateField';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import { RecurringTransferActionType, SetAccountType } from '../form/actions';
import { isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';

const RecurringTransferMobileFields: React.FC<FieldProps> = ({
    form,
    locked,
    shouldFocusFirstElement,
    freeWithdrawals,
    accounts,
    accountState,
    signingOperationPending,
    lockToAccount = false,
}) => {
    const translated = useTranslation();
    const { state, errors, handleChange, curryChange } = form;
    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();
    const fromAccounts = accounts?.filter(isValidAccountTransferFrom) ?? [];
    const invalidAccounts = filterInvalidAccounts({ allAccounts: accounts, validAccounts: fromAccounts });
    const { featureToggles } = useConfig();
    return (
        <FormFieldset disabled={signingOperationPending}>
            <AmountBox isNoCoverage={isNoCoverage(state, targetFromAccount)} error={errors.amount} locked={locked}>
                <AmountInput
                    value={state.amount}
                    ariaLabel={translated.transferAmount}
                    onChange={curryChange(RecurringTransferActionType.AMOUNT)}
                    locked={locked}
                    focusFirst={shouldFocusFirstElement}
                />
            </AmountBox>
            <SelectList>
                <li>
                    <AccountField
                        id="recurringTransferFromAccount"
                        buttonLabel={translated.from}
                        title={translated.transferFromAccount}
                        account={targetFromAccount}
                        accountNumber={state.fromAccount}
                        accounts={fromAccounts ?? []}
                        invalidAccounts={invalidAccounts}
                        error={errors.accountId}
                        onChange={(accountNumber) => {
                            handleChange({
                                type: RecurringTransferActionType.ACCOUNT_SELECTED,
                                payload: {
                                    setAccountType: SetAccountType.SET_FROM_ACCOUNT,
                                    accountNumber,
                                },
                            });
                        }}
                        disabled={signingOperationPending}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            handleChange({
                                type: showModal
                                    ? RecurringTransferActionType.SHOW_FROM_ACCOUNTS
                                    : RecurringTransferActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        placeholder={translated.transferFromAccount}
                        locked={locked}
                        bottomLabel={
                            freeWithdrawals.show ? (
                                <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                            ) : undefined
                        }
                    />
                </li>
                <li>
                    <AccountField
                        id="recurringTransferToAccount"
                        buttonLabel={translated.to}
                        dataTestLabel="to"
                        title={translated.transferToAccount}
                        account={targetToAccount}
                        accountNumber={state.toAccount}
                        accounts={accounts?.filter(isValidAccountTransferTo) ?? []}
                        onChange={(accountNumber) =>
                            handleChange({
                                type: RecurringTransferActionType.ACCOUNT_SELECTED,
                                payload: {
                                    setAccountType: SetAccountType.SET_TO_ACCOUNT,
                                    accountNumber,
                                },
                            })
                        }
                        disabled={signingOperationPending}
                        error={errors.toAccount}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showToAccounts}
                        setShowModal={(showModal: boolean) => {
                            handleChange({
                                type: showModal
                                    ? RecurringTransferActionType.SHOW_TO_ACCOUNTS
                                    : RecurringTransferActionType.HIDE_TO_ACCOUNTS,
                            });
                        }}
                        placeholder={translated.transferToAccount}
                        locked={locked || lockToAccount}
                    />
                </li>
                <li>
                    <NoteField
                        onChange={curryChange(RecurringTransferActionType.NOTE)}
                        onChangeOwnText={
                            featureToggles.disableOwnTransferText || featureToggles.disableOwnText
                                ? undefined
                                : curryChange(RecurringTransferActionType.OWNTEXT)
                        }
                        note={state.note}
                        ownText={state.ownText}
                        ownTextLabel={translated.transferAddOwnText}
                        labelText={translated.amountNote[NoteTypes.MESSAGE].label}
                        placeholder={translated.amountNote[NoteTypes.MESSAGE].placeholder}
                        locked={locked}
                        disabled={signingOperationPending}
                        forceDisableKid
                    />
                </li>
                <li>
                    <DateField
                        label={translated.date}
                        minDate={state.firstBankingDate}
                        date={state.firstPaymentDate}
                        paymentInterval={state.paymentInterval}
                        endDate={state.lastPaymentDate}
                        onlyBusinessDays
                        paymentAgreement
                        recurringTransfer
                        locked={locked}
                        disabled={signingOperationPending}
                        onChange={curryChange(RecurringTransferActionType.FIRST_PAYMENT_DATE)}
                        onChangePaymentInterval={curryChange(RecurringTransferActionType.PAYMENT_INTERVAL)}
                        onSetEndDate={curryChange(RecurringTransferActionType.LAST_PAYMENT_DATE)}
                    />
                </li>
            </SelectList>
        </FormFieldset>
    );
};

export default RecurringTransferMobileFields;
